
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { useAuth } from './AuthContext';
import { authAPI, API_BASE_URL } from '../services/api';
import { useToast } from '@chakra-ui/react';
import { AppLauncher } from '@capacitor/app-launcher';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import BackgroundService from '../utils/backgroundService';
import GPSDisclosureModal from '../components/GPSDisclosureModal';
import { getStoredTokenForAudience } from '../utils/authStorage';

const GPSContext = createContext(null);

export const GPSProvider = ({ children }) => {
    const { user } = useAuth();
    const [gpsStatus, setGpsStatus] = useState('IDLE'); // IDLE, SEARCHING, CONNECTED, ERROR, RETRYING
    const [gpsInfo, setGpsInfo] = useState({});
    const [isTracking, setIsTracking] = useState(false);

    // Disclosure Modal State
    const [isDisclosureOpen, setIsDisclosureOpen] = useState(false);
    const [disclosureResolver, setDisclosureResolver] = useState(null);

    // Internal State
    const watchIdRef = useRef(null);
    const activeTrackingIdsRef = useRef([]);
    const lastUpdateRef = useRef(0);
    const startTimeRef = useRef(0); // For Burst Mode
    const toast = useToast();

    // Watchdog
    useEffect(() => {
        const watchdog = setInterval(() => {
            if (activeTrackingIdsRef.current.length > 0) {
                const now = Date.now();
                const timeSinceLastUpdate = now - lastUpdateRef.current;
                const threshold = (gpsStatus === 'CONNECTED') ? 40000 : 20000; // Slightly relaxed

                if (timeSinceLastUpdate > threshold && watchIdRef.current !== null) {
                    console.warn(`[GPS Watchdog] No update for ${timeSinceLastUpdate}ms. Restarting...`);
                    setGpsStatus('RETRYING');
                    restartGPS();
                }
            }
        }, 5000);
        return () => clearInterval(watchdog);
    }, [gpsStatus]);

    const stopGPS = async () => {
        if (watchIdRef.current !== null) {
            console.log("Stopping GPS Watch ID:", watchIdRef.current);
            try {
                if (Capacitor.isNativePlatform()) {
                    await Geolocation.clearWatch({ id: watchIdRef.current });

                    if (Capacitor.getPlatform() === 'android') {
                        BackgroundService.stop().catch(console.error);
                    }
                } else {
                    navigator.geolocation.clearWatch(watchIdRef.current);
                }
            } catch (e) {
                console.error("Error clearing watch:", e);
            }
            watchIdRef.current = null;
            setGpsStatus('IDLE');
            setIsTracking(false);
        }
    };

    const restartGPS = async () => {
        await stopGPS();
        setTimeout(() => startGPS(), 1000);
    };

    const startTracking = (studentIds) => {
        // Update targets
        const currentIds = activeTrackingIdsRef.current;
        const newIds = [...new Set([...currentIds, ...studentIds])];
        activeTrackingIdsRef.current = newIds;

        if (newIds.length > 0) {
            if (watchIdRef.current === null) {
                startGPS();
            } else {
                // Already running, update native config
                updateNativeConfig();
            }
        }
    };

    const stopTracking = (studentId) => {
        const newIds = activeTrackingIdsRef.current.filter(id => id !== studentId);
        activeTrackingIdsRef.current = newIds;

        if (newIds.length === 0) {
            stopGPS();
        } else {
            // Still running, update native config to remove ID
            updateNativeConfig();
        }
    };

    // Helper to send config to Native Service
    const updateNativeConfig = async () => {
        if (Capacitor.getPlatform() === 'android') {
            try {
                // C3 auth 翻新：authStorage.js 的 token 讀取改為 async（@capacitor/preferences，
                // 見 utils/authStorage.js），這裡本來就在 async 函式內，補一個 await 即可。
                const token = await getStoredTokenForAudience();
                if (!token) {
                    console.warn("No auth token, cannot enable background native upload");
                    return;
                }
                const ids = activeTrackingIdsRef.current.join(",");
                const apiUrl = `${API_BASE_URL}/api/v1/pickups/gps/update`; // 修：原本漏 /api/v1 → 原生背景上傳一直 404

                await BackgroundService.updateConfig({
                    authToken: token,
                    studentIds: ids,
                    apiUrl: apiUrl
                });
                console.log("[Native Config] Updated:", ids);
            } catch (e) {
                console.warn("Failed to update native config", e);
            }
        }
    }

    // Sync with backend status (called by UI when data fetched)
    const syncTrackingStudents = (students) => {
        const enRoute = students.filter(s => s.status === 'PARENT_EN_ROUTE').map(s => s.id);
        const prevLen = activeTrackingIdsRef.current.length;
        activeTrackingIdsRef.current = enRoute;

        if (enRoute.length > 0) {
            if (watchIdRef.current === null) {
                startGPS();
            } else if (prevLen !== enRoute.length) {
                updateNativeConfig();
            }
        } else {
            if (watchIdRef.current !== null) stopGPS();
        }
    };

    const startGPS = async () => {
        setGpsStatus(prev => prev === 'RETRYING' ? 'RETRYING' : 'SEARCHING');
        lastUpdateRef.current = 0; // Allow immediate first update
        startTimeRef.current = Date.now(); // Start Burst Mode timer
        setIsTracking(true);

        if (Capacitor.isNativePlatform()) {
            try {
                // 1. Check Permissions FIRST
                let permStatus = await Geolocation.checkPermissions();

                // 2. Android Prominent Disclosure (only if not granted)
                if (permStatus.location !== 'granted' && Capacitor.getPlatform() === 'android') {
                    const userAgreed = await new Promise((resolve) => {
                        setDisclosureResolver(() => resolve);
                        setIsDisclosureOpen(true);
                    });

                    setIsDisclosureOpen(false);
                    if (!userAgreed) {
                        toast({ title: "已取消定位設定", status: "info" });
                        setGpsStatus('IDLE');
                        setIsTracking(false);
                        return;
                    }
                }

                // 3. Request Permissions
                if (permStatus.location !== 'granted') {
                    permStatus = await Geolocation.requestPermissions();
                    if (permStatus.location !== 'granted') {
                        toast({ title: "無法取得定位權限", description: "背景服務無法啟動", status: "error" });
                        setGpsStatus('ERROR');
                        return;
                    }
                }

                // 4. Battery Optimization Check (Android)
                if (Capacitor.getPlatform() === 'android') {
                    try {
                        const batRes = await BackgroundService.checkBatteryOptimization();
                        if (!batRes.isIgnoring) {
                            // Consider showing a custom dialog explaining WHY first, but for now request directly as per instruction
                            await BackgroundService.requestIgnoreBatteryOptimization();
                        }
                    } catch (e) {
                        console.warn("Battery Optimization Check Failed:", e);
                    }

                    // 3. Start Android Background Service
                    try {
                        // Request Notification Permission for Android 13+
                        await FirebaseMessaging.requestPermissions();
                    } catch (e) {
                        console.warn("Firebase Messaging permission request failed", e);
                    }

                    try {
                        console.log("Starting Android Background Service...");
                        await BackgroundService.start();
                        // Send Initial Config
                        setTimeout(() => updateNativeConfig(), 500); // Small delay to ensure service start
                    } catch (e) {
                        console.error("Failed to start background service:", e);
                    }
                }

                // 3. (Phase 2-B) 移除 Fast Start：原 getCurrentPosition(maximumAge:30000) 會回傳舊快取，
                //    導致第一筆位置是舊的 → ETA 算錯。改由下方 watchPosition 提供第一筆即時位置；
                //    後端開場緩衝(Phase 1-A)會在前 30 秒顯示「定位中...」，不受影響。

                // 4. Start Watch (High Accuracy)
                const id = await Geolocation.watchPosition(
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 3000
                    },
                    async (position, err) => {
                        handlePositionUpdate(position, err);
                    }
                );
                watchIdRef.current = id;
            } catch (err) {
                console.error("Native GPS Error:", err);
                setGpsStatus('ERROR');
            }
        } else {
            // Web Fallback (removed for brevity in this replacement chunk, assume unchanged logic needs to be preserved if I'm replacing the whole function block. Wait, the instruction said REPLACE content. I must include the else block if I'm replacing the whole function.)
            if (!navigator.geolocation) {
                console.error("Geolocation not supported");
                setGpsStatus('ERROR');
                return;
            }
            watchIdRef.current = navigator.geolocation.watchPosition(
                (pos) => handlePositionUpdate(pos, null),
                (err) => handlePositionUpdate(null, err),
                { enableHighAccuracy: true }
            );
        }
    };

    const handlePositionUpdate = async (position, err) => {
        if (err) {
            console.error("GPS Error Callback:", err);
            // Watchdog will handle restart if stuck
            return;
        }
        if (!position) return;

        setGpsStatus('CONNECTED');
        const { latitude, longitude, accuracy } = position.coords;
        const now = Date.now();

        // Filters
        if (accuracy > 1000) return; // Allow coarser initial signal (was 500)

        // (Phase 2-A) Thin Client：移除前端動態節流（burst / 距離判斷），改由後端決定 ETA 計算頻率
        // （後端 30s/100m 快取 + 開場緩衝 + 精度把關）。前端僅保留固定最小上傳間隔，避免請求過量。
        const MIN_UPLOAD_INTERVAL = 8000; // 8 秒（老師端無地圖、只看 ETA → 不需更密；省 DB 寫入）
        if (now - lastUpdateRef.current < MIN_UPLOAD_INTERVAL && lastUpdateRef.current !== 0) return;

        lastUpdateRef.current = now;

        // Upload
        const targets = activeTrackingIdsRef.current;
        let anySuccess = false;

        for (const studentId of targets) {
            try {
                const res = await authAPI.updateGps({
                    student_id: parseInt(studentId),
                    latitude, longitude, accuracy: Math.round(accuracy)
                });

                if (res.data?.success) {
                    setGpsInfo(prev => ({
                        ...prev,
                        [studentId]: {
                            eta: res.data.eta_minutes,
                            distance: res.data.distance_meters,
                            lastUpdate: new Date()
                        }
                    }));
                    anySuccess = true;

                    // Auto-stop if close
                    if (res.data.distance_meters < 50) {
                        // We could notify user here
                    }
                }
            } catch (e) {
                console.error(`GPS Upload Error (${studentId}):`, e);
            }
        }

        if (anySuccess) {
            // User requested notification on upload
            // We can show a small non-intrusive toast or console log
            console.log("📍 GPS Uploaded Successfully");
            // Optional: Haptic feedback?
        }
    };

    return (
        <GPSContext.Provider value={{
            gpsStatus, gpsInfo, isTracking,
            syncTrackingStudents, startTracking, stopTracking
        }}>
            {children}
            <GPSDisclosureModal
                isOpen={isDisclosureOpen}
                onAccept={() => disclosureResolver?.(true)}
                onDecline={() => disclosureResolver?.(false)}
            />
        </GPSContext.Provider>
    );
};

export const useGPS = () => useContext(GPSContext);
